/**
 * R-235 — "Move to step…" modal for supervisors/admins.
 *
 * Two-stage UX:
 *   Stage 1 — Target picker: lists allowed pipeline targets fetched from
 *             GET /api/batches/:id/move-targets. Each row shows the human
 *             step label + the server-computed impact string.
 *   Stage 2 — Impact confirmation: ConfirmModal naming the impact + optional
 *             active-operator warning + optional reason field.
 *
 * Error handling:
 *   - 409 → blocking-file message shown inline (picker stays open).
 *   - 403 → "not permitted" message shown inline.
 *   - Other → falls back to the standard getApiErrorMessage extraction.
 */
import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api-client";
import { formatStep } from "@/lib/formatters";
import { getApiErrorMessage } from "@/lib/error-toast";
import "./BatchCheckInModal.css";

// ─── Types ────────────────────────────────────────────────────────────────────

interface MoveTarget {
  stepType: string;
  order: number;
  impact: string;
}

interface MoveTargetsResponse {
  currentStep: string;
  targets: MoveTarget[];
}

interface Props {
  batchId: string;
  /** Number of documents currently locked to an operator (assignedTo != null). */
  activeOperatorCount: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Stage type ───────────────────────────────────────────────────────────────

type Stage = "loading" | "picker" | "confirm" | "submitting";

// ─── Component ────────────────────────────────────────────────────────────────

export function MoveStepModal({
  batchId,
  activeOperatorCount,
  open,
  onClose,
  onSuccess,
}: Props) {
  const [stage, setStage] = useState<Stage>("loading");
  const [targets, setTargets] = useState<MoveTarget[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<MoveTarget | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset and fetch targets whenever the modal opens.
  useEffect(() => {
    if (!open) return;

    setStage("loading");
    setSelectedTarget(null);
    setReason("");
    setError(null);
    setTargets([]);

    let cancelled = false;

    api
      .get<MoveTargetsResponse>(`/batches/${batchId}/move-targets`)
      .then((res) => {
        if (cancelled) return;
        setTargets(res.targets);
        setStage("picker");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getApiErrorMessage(err, { fallback: "Failed to load move targets." }));
        setStage("picker");
      });

    return () => {
      cancelled = true;
    };
  }, [open, batchId]);

  if (!open) return null;

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleSelectTarget(target: MoveTarget) {
    setSelectedTarget(target);
    setError(null);
    setStage("confirm");
  }

  function handleBack() {
    setSelectedTarget(null);
    setError(null);
    setStage("picker");
  }

  async function handleConfirm() {
    if (!selectedTarget) return;
    setStage("submitting");
    setError(null);

    const body: { targetStep: string; reason?: string } = {
      targetStep: selectedTarget.stepType,
    };
    if (reason.trim()) body.reason = reason.trim();

    try {
      await api.post(`/batches/${batchId}/move-step`, body);
      onSuccess();
    } catch (err) {
      // 409 — blocking export conflict; extract location from body.
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as Record<string, unknown> | null;
        const location =
          typeof body?.["location"] === "string" ? body["location"] : null;
        const baseMsg =
          typeof body?.["message"] === "string"
            ? body["message"]
            : "Output already exists — resolve before moving.";
        setError(location ? `${baseMsg} (${location})` : baseMsg);
        setStage("confirm");
        return;
      }

      // 403 — permission denied (shouldn't normally happen; UI gates it).
      if (err instanceof ApiError && err.status === 403) {
        setError("Not permitted: you do not have permission to move this batch.");
        setStage("confirm");
        return;
      }

      setError(getApiErrorMessage(err, { fallback: "Move failed. Please try again." }));
      setStage("confirm");
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const isConfirmStage = stage === "confirm" || stage === "submitting";
  const isSubmitting = stage === "submitting";

  // Build the confirm description: impact text + optional operator warning.
  const confirmDescription = selectedTarget
    ? [
        selectedTarget.impact,
        activeOperatorCount > 0
          ? `${activeOperatorCount} operator${activeOperatorCount === 1 ? "" : "s"} currently working on this batch will be returned to their queue.`
          : null,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <div
      className="batch-checkin-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        className="batch-checkin-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="move-step-modal-title"
      >
        <div className="batch-checkin-modal__header">
          <h3 id="move-step-modal-title" className="batch-checkin-modal__title">
            {isConfirmStage
              ? `Move to ${selectedTarget ? formatStep(selectedTarget.stepType) : "step"}?`
              : "Move to step…"}
          </h3>
        </div>

        <div className="batch-checkin-modal__body">
          {/* ── Picker stage ── */}
          {(stage === "loading" || stage === "picker") && (
            <>
              {stage === "loading" && (
                <p className="batch-checkin-modal__description">
                  Loading available steps…
                </p>
              )}

              {stage === "picker" && targets.length === 0 && !error && (
                <p className="batch-checkin-modal__description">
                  No steps available to move to.
                </p>
              )}

              {stage === "picker" && targets.length > 0 && (
                <ul className="batch-checkin-modal__step-list">
                  {targets.map((target) => (
                    <li
                      key={target.stepType}
                      className="batch-checkin-modal__step-item"
                    >
                      <button
                        type="button"
                        data-testid={`move-target-${target.stepType}`}
                        className="batch-checkin-modal__btn-submit batch-checkin-modal__step-btn"
                        onClick={() => handleSelectTarget(target)}
                      >
                        {formatStep(target.stepType)}
                      </button>
                      <p className="batch-checkin-modal__step-impact">
                        {target.impact}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* ── Confirm stage ── */}
          {isConfirmStage && selectedTarget && (
            <>
              <p className="batch-checkin-modal__description">
                {confirmDescription}
              </p>

              <div className="batch-checkin-modal__field">
                <label
                  className="batch-checkin-modal__label"
                  htmlFor="move-step-reason-input"
                >
                  Reason (optional)
                </label>
                <textarea
                  id="move-step-reason-input"
                  data-testid="move-step-reason"
                  className="batch-checkin-modal__textarea"
                  maxLength={500}
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why this move is needed (recorded in the audit trail)"
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}

          {/* ── Error display (both stages) ── */}
          {error && (
            <div
              className="batch-checkin-modal__error"
              role="alert"
            >
              {error}
            </div>
          )}
        </div>

        <div className="batch-checkin-modal__actions">
          {isConfirmStage ? (
            <>
              <button
                type="button"
                className="batch-checkin-modal__btn-cancel"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="button"
                className="batch-checkin-modal__btn-submit"
                data-testid="move-step-confirm-btn"
                onClick={() => void handleConfirm()}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Moving…" : "Move"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="batch-checkin-modal__btn-cancel"
              onClick={onClose}
              disabled={stage === "loading"}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
